// src/components/shared/toast/ToastViewport.tsx
//
// The single subscriber to the toast store. Mount it once, near the root.
import React, { useSyncExternalStore, useCallback } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaExclamationTriangle, FaInfoCircle, FaTimes } from 'react-icons/fa';
import { subscribe, getToasts, dismiss, type Toast, type ToastVariant } from './store';

const ICONS: Record<ToastVariant, React.ComponentType<{ className?: string }>> = {
  success: FaCheckCircle,
  error: FaExclamationCircle,
  warning: FaExclamationTriangle,
  info: FaInfoCircle,
};

const ACCENT: Record<ToastVariant, string> = {
  success: 'text-success border-l-success',
  error: 'text-danger border-l-danger',
  warning: 'text-warning border-l-warning',
  info: 'text-info border-l-info',
};

const ToastCard = ({ toast }: { toast: Toast }) => {
  const Icon = ICONS[toast.variant];
  const onDismiss = useCallback(() => dismiss(toast.id), [toast.id]);

  return (
    <div
      // Errors are announced assertively; the rest wait for a pause in speech.
      role={toast.variant === 'error' ? 'alert' : 'status'}
      className={`glass pointer-events-auto w-full sm:w-80 p-3 pl-3.5 border-l-4
        flex items-start gap-2.5 animate-slide-up ${ACCENT[toast.variant]}`}
    >
      <Icon className="mt-[3px] flex-shrink-0 text-sm" />
      <div className="min-w-0 flex-1">
        {toast.title ? (
          <div className="text-sm font-semibold text-text leading-snug">{toast.title}</div>
        ) : null}
        <div className={`text-xs leading-relaxed break-words ${toast.title ? 'text-text-secondary mt-0.5' : 'text-text'}`}>
          {toast.message}
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="flex-shrink-0 p-1 -m-1 rounded text-text-muted hover:text-text transition-colors"
      >
        <FaTimes className="text-[11px]" />
      </button>
    </div>
  );
};

const ToastViewport = () => {
  const toasts = useSyncExternalStore(subscribe, getToasts, getToasts);

  if (!toasts.length) return null;

  return (
    <div
      // `pointer-events-none` on the stack so the fixed overlay never swallows
      // clicks on the page behind it; each card re-enables them for itself.
      className="fixed z-50 pointer-events-none flex flex-col gap-2
        bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-80"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} />
      ))}
    </div>
  );
};

export default ToastViewport;
